import { Observable } from 'rxjs';
import { APIResponseWrapper } from '../../models/common/api-response-wrapper';
import * as HrModels from '../hr.models';
import { Constructor, HrApiClient } from '../shared/hr-api-client';

export function withHrLaborApi<TBase extends Constructor<HrApiClient>>(Base: TBase) {
  return class extends Base {
    laborSummary(owningCompanyId?: number): Observable<APIResponseWrapper<HrModels.LaborSummary>> {
      return this.http.get<APIResponseWrapper<HrModels.LaborSummary>>(`${this.baseUrl}/labor/summary`, {
        params: this.params({ owningCompanyId })
      });
    }

    laborProfiles(owningCompanyId?: number): Observable<APIResponseWrapper<HrModels.LaborProfile[]>> {
      return this.http.get<APIResponseWrapper<HrModels.LaborProfile[]>>(`${this.baseUrl}/labor/profiles`, {
        params: this.params({ owningCompanyId })
      });
    }

    saveLaborProfile(request: HrModels.LaborProfile): Observable<APIResponseWrapper<HrModels.LaborProfile>> {
      return request.laborProfileId
        ? this.http.put<APIResponseWrapper<HrModels.LaborProfile>>(`${this.baseUrl}/labor/profiles/${request.laborProfileId}`, request)
        : this.http.post<APIResponseWrapper<HrModels.LaborProfile>>(`${this.baseUrl}/labor/profiles`, request);
    }

    saveLaborSkill(profileId: number, request: HrModels.LaborSkill): Observable<APIResponseWrapper<HrModels.LaborSkill>> {
      return request.employeeSkillId
        ? this.http.put<APIResponseWrapper<HrModels.LaborSkill>>(`${this.baseUrl}/labor/profiles/${profileId}/skills/${request.employeeSkillId}`, request)
        : this.http.post<APIResponseWrapper<HrModels.LaborSkill>>(`${this.baseUrl}/labor/profiles/${profileId}/skills`, request);
    }

    laborProficiencyLevels(owningCompanyId?: number): Observable<APIResponseWrapper<HrModels.LaborProficiencyLevel[]>> {
      return this.http.get<APIResponseWrapper<HrModels.LaborProficiencyLevel[]>>(`${this.baseUrl}/labor/proficiency-levels`, {
        params: this.params({ owningCompanyId })
      });
    }

    saveLaborProficiencyLevel(request: HrModels.LaborProficiencyLevel): Observable<APIResponseWrapper<HrModels.LaborProficiencyLevel>> {
      return request.proficiencyLevelId
        ? this.http.put<APIResponseWrapper<HrModels.LaborProficiencyLevel>>(`${this.baseUrl}/labor/proficiency-levels/${request.proficiencyLevelId}`, request)
        : this.http.post<APIResponseWrapper<HrModels.LaborProficiencyLevel>>(`${this.baseUrl}/labor/proficiency-levels`, request);
    }

    laborRateCards(owningCompanyId?: number): Observable<APIResponseWrapper<HrModels.LaborRateCard[]>> {
      return this.http.get<APIResponseWrapper<HrModels.LaborRateCard[]>>(`${this.baseUrl}/labor/rate-cards`, {
        params: this.params({ owningCompanyId })
      });
    }

    saveLaborRateCard(request: HrModels.LaborRateCard): Observable<APIResponseWrapper<HrModels.LaborRateCard>> {
      return request.laborRateCardId
        ? this.http.put<APIResponseWrapper<HrModels.LaborRateCard>>(`${this.baseUrl}/labor/rate-cards/${request.laborRateCardId}`, request)
        : this.http.post<APIResponseWrapper<HrModels.LaborRateCard>>(`${this.baseUrl}/labor/rate-cards`, request);
    }

    laborWorkOrders(filters: { owningCompanyId?: number; targetType?: string; targetId?: number } = {}):
      Observable<APIResponseWrapper<HrModels.LaborWorkOrder[]>> {
      return this.http.get<APIResponseWrapper<HrModels.LaborWorkOrder[]>>(`${this.baseUrl}/labor/work-orders`, {
        params: this.params(filters)
      });
    }

    saveLaborWorkOrder(request: HrModels.LaborWorkOrder): Observable<APIResponseWrapper<HrModels.LaborWorkOrder>> {
      return request.laborWorkOrderId
        ? this.http.put<APIResponseWrapper<HrModels.LaborWorkOrder>>(`${this.baseUrl}/labor/work-orders/${request.laborWorkOrderId}`, request)
        : this.http.post<APIResponseWrapper<HrModels.LaborWorkOrder>>(`${this.baseUrl}/labor/work-orders`, request);
    }

    laborCandidates(workOrderId: number, filters: { owningCompanyId?: number; ownershipType?: string; location?: string } = {}):
      Observable<APIResponseWrapper<HrModels.LaborCandidate[]>> {
      return this.http.get<APIResponseWrapper<HrModels.LaborCandidate[]>>(`${this.baseUrl}/labor/work-orders/${workOrderId}/candidates`, {
        params: this.params(filters)
      });
    }

    laborAssignments(owningCompanyId?: number, workOrderId?: number): Observable<APIResponseWrapper<HrModels.LaborAssignment[]>> {
      return this.http.get<APIResponseWrapper<HrModels.LaborAssignment[]>>(`${this.baseUrl}/labor/assignments`, {
        params: this.params({ owningCompanyId, workOrderId })
      });
    }

    saveLaborAssignment(request: HrModels.LaborAssignment): Observable<APIResponseWrapper<HrModels.LaborAssignment>> {
      return request.laborAssignmentId
        ? this.http.put<APIResponseWrapper<HrModels.LaborAssignment>>(`${this.baseUrl}/labor/assignments/${request.laborAssignmentId}`, request)
        : this.http.post<APIResponseWrapper<HrModels.LaborAssignment>>(`${this.baseUrl}/labor/assignments`, request);
    }

    laborWorkEntries(owningCompanyId?: number, assignmentId?: number): Observable<APIResponseWrapper<HrModels.LaborWorkEntry[]>> {
      return this.http.get<APIResponseWrapper<HrModels.LaborWorkEntry[]>>(`${this.baseUrl}/labor/work-entries`, {
        params: this.params({ owningCompanyId, assignmentId })
      });
    }

    saveLaborWorkEntry(request: HrModels.LaborWorkEntry): Observable<APIResponseWrapper<HrModels.LaborWorkEntry>> {
      return request.laborWorkEntryId
        ? this.http.put<APIResponseWrapper<HrModels.LaborWorkEntry>>(`${this.baseUrl}/labor/work-entries/${request.laborWorkEntryId}`, request)
        : this.http.post<APIResponseWrapper<HrModels.LaborWorkEntry>>(`${this.baseUrl}/labor/work-entries`, request);
    }

    submitLaborWorkEntry(id: number): Observable<APIResponseWrapper<HrModels.LaborWorkEntry>> {
      return this.http.post<APIResponseWrapper<HrModels.LaborWorkEntry>>(`${this.baseUrl}/labor/work-entries/${id}/submit`, {});
    }

    approveLaborWorkEntry(id: number): Observable<APIResponseWrapper<HrModels.LaborWorkEntry>> {
      return this.http.post<APIResponseWrapper<HrModels.LaborWorkEntry>>(`${this.baseUrl}/labor/work-entries/${id}/approve`, {});
    }

    rejectLaborWorkEntry(id: number, reason: string): Observable<APIResponseWrapper<HrModels.LaborWorkEntry>> {
      return this.http.post<APIResponseWrapper<HrModels.LaborWorkEntry>>(`${this.baseUrl}/labor/work-entries/${id}/reject`, { reason });
    }

    reverseLaborWorkEntry(id: number, reason: string): Observable<APIResponseWrapper<HrModels.LaborWorkEntry>> {
      return this.http.post<APIResponseWrapper<HrModels.LaborWorkEntry>>(`${this.baseUrl}/labor/work-entries/${id}/reverse`, { reason });
    }

    laborFinanceExceptions(owningCompanyId?: number): Observable<APIResponseWrapper<HrModels.LaborFinanceLink[]>> {
      return this.http.get<APIResponseWrapper<HrModels.LaborFinanceLink[]>>(`${this.baseUrl}/labor/finance/exceptions`, {
        params: this.params({ owningCompanyId })
      });
    }

    laborSettings(owningCompanyId?: number): Observable<APIResponseWrapper<HrModels.LaborSettings>> {
      return this.http.get<APIResponseWrapper<HrModels.LaborSettings>>(`${this.baseUrl}/labor/settings`, {
        params: this.params({ owningCompanyId })
      });
    }

    saveLaborSettings(request: HrModels.LaborSettings): Observable<APIResponseWrapper<HrModels.LaborSettings>> {
      return this.http.put<APIResponseWrapper<HrModels.LaborSettings>>(`${this.baseUrl}/labor/settings`, request);
    }
  };
}
